import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { RoomInfo } from '@shared/api/roomInfo/roomInfo.interface';
import { receiverNameWithHonorific } from '@shared/utils/receiverName.util';

import {
  ShopOrderForSeller,
  ShopOrderPaymentProvider,
  ShopOrderSellerRequestStatus,
} from './shopOrder.interface';

//* PG 자동환불이 안 되는 결제(가상계좌·무통장 계열) 판별 — 면세계산서/수기환불 요청 대상.
//* method는 자유 문자열('카드'·'가상계좌'·'계좌 입금'·레거시 'vbank')이라 키워드 포함으로 판별한다.
export const DEPOSIT_LIKE_METHOD_KEYWORDS = ['가상', '계좌', '입금'] as const;

export const isDepositLikePayment = (paymentDetail?: { method?: string }): boolean => {
  const method = paymentDetail?.method ?? '';
  if (!method) return false;
  if (method.toLowerCase().includes('vbank')) return true;
  return DEPOSIT_LIKE_METHOD_KEYWORDS.some((keyword) => method.includes(keyword));
};

//* 토스 가상계좌 — 환불받을 계좌를 함께 보내면 PG 자동취소가 되는 유일한 입금성 결제다.
//* 부트페이 가상계좌·무통장·'계좌 입금'은 여전히 수기환불(운영사 직접 이체) 대상.
//* 판정은 문자열 휴리스틱(isDepositLikePayment)이 아니라 DB에 기록된 provider+method 정확 일치로만 한다 —
//* 어긋나면 계좌를 안 받고 취소를 시도해 토스가 거절하거나, 반대로 필요없는 계좌를 묻게 된다.
//! 목록 프로젝션(select)에 paymentDetail이 빠지면 이 판정이 조용히 false가 된다.
export const isTossVbankPayment = (paymentDetail?: {
  provider?: ShopOrderPaymentProvider;
  method?: string;
}): boolean =>
  paymentDetail?.provider === ShopOrderPaymentProvider.Tosspayments &&
  paymentDetail?.method === '가상계좌';

/**
 * 취소 API 실패 문구. 서버는 거절 사유를 담당자가 읽을 수 있는 한국어 `message`로 내려준다
 * (계좌 누락·은행 미인식·이중환불 차단·토스 거절 등).
 *! `error` 코드로 분기하면 안 된다 — 전역 HttpExceptionFilter가 `error`를 예외 클래스명으로
 *! 덮어써서 `MANUAL_REFUND_IN_PROGRESS` 같은 코드는 프론트에 도달하지 않는다. 400의 message만 쓴다.
 *! 그 외 상태코드의 message는 내부 문구(_assert 영문·axios 원문)라 사용자에게 쓸 수 없다.
 */
export const shopOrderCancelErrorMessage = (
  err: unknown,
  fallback = '취소에 실패했습니다.',
): string => {
  const response = (
    err as { response?: { status?: number; data?: { message?: string } } }
  )?.response;
  if (response?.status === 400 && response.data?.message) return response.data.message;
  switch (response?.status) {
    case 403:
      return '권한이 없습니다.';
    case 404:
      return '주문을 찾을 수 없습니다.';
    default:
      return fallback;
  }
};

export const sellerRequestStatusLabel: Record<ShopOrderSellerRequestStatus, string> = {
  Requested: '대기',
  Done: '완료',
  Rejected: '반려',
};

//* Bugo.fevent 타입(Fevent 제네릭 기본값)은 funeralHomeInfo·roomInfo ref를 string으로 두지만,
//* 셀러 목록·과거배송검색 쿼리가 populate하므로 런타임엔 객체가 온다. 미populate면 undefined.
const asPopulated = function <T>(value: T | string | undefined): T | undefined {
  return typeof value === 'object' && value !== null ? value : undefined;
};

//* 부고(fevent) 기준 현재 상가 주소 — 주문 생성 시 receiverAddress 조합식과 동일
//* (ShopItemCheckoutPage 참조). 호실이동으로 fevent가 갱신됐다면 새 호실이 나온다.
//* bugo 없음(전화주문 등)·주소 정보 미populate면 undefined.
export const feventCurrentAddress = (
  shopOrder: ShopOrderForSeller,
): string | undefined => {
  const fevent = shopOrder.bugo?.fevent;
  if (!fevent) return undefined;
  const funeralHome = asPopulated<FuneralHomeInfo>(fevent.funeralHomeInfo);
  const room = asPopulated<RoomInfo>(fevent.roomInfo);
  const address = fevent.funeralHomeInfoEmbed?.address ?? funeralHome?.address;
  if (!address) return undefined;
  const homeName = funeralHome?.name ?? fevent.funeralHomeInfoEmbed?.name ?? '';
  const roomName = fevent.roomInfoEmbed?.name ?? room?.name ?? '';
  return `${address}, [${homeName}, ${roomName}]`;
};

//* 쉼표·공백 등 표기 차이만 다른 주소를 같다고 보기 위한 정규화
//* (구 조합식은 괄호 앞 ', ' 구분자가 없어 "…(독곡동)[제일장례식장, 202호실]" 형태로 저장됨)
const normalizeAddress = (address: string): string => address.replace(/[\s,]/g, '');

//* 부고 기준 현재 상가 주소와 주문에 저장된 배송지 주소의 불일치 여부 (호실이동 감지)
export const feventAddressMismatch = (shopOrder: ShopOrderForSeller): boolean => {
  const feventAddress = feventCurrentAddress(shopOrder);
  return (
    feventAddress !== undefined &&
    normalizeAddress(feventAddress) !==
      normalizeAddress(shopOrder.deliveryDetail.receiverAddress)
  );
};

export const orderContentString = (shopOrder: ShopOrderForSeller) => {
  return (
    `${shopOrder.orderDetail.shopItemEmbed.name}\n\n` +
    `주소: ${shopOrder.deliveryDetail.receiverAddress}\n` +
    `문구(보내는분): ${shopOrder.bugoDetail.senderPhrase}\n` +
    `조의문구: ${shopOrder.bugoDetail.condolencePhrase}\n` +
    //* receiverName은 이미 "OOO 상주님"으로 저장돼 있다 — 덧붙이면 "상주님 상주님"이 된다
    `받으시는분: ${receiverNameWithHonorific(shopOrder.deliveryDetail.receiverName)}\n` +
    `도매가: ${shopOrder.orderDetail.shopItemEmbed.priceWhole.toLocaleString()}원`
  );
};
